﻿import { paymentStatusInfo, paymentTypeLabel, paymentMethodColor } from "@/services/payment/appointmentPaymentStatus";
import { useCustomTable } from "@/hooks/use-custom-table";
import {
    AppointmentStatus,
    BankGateway,
    CustomerDto,
    MerchantAppointmentQueueDto,
    MerchantUserDto,
} from "@/models/club";
import { useGetAppointmentDeposit } from "@/services/appointment/hooks/useGetAppointmentDeposit";
import {
    ActionIcon,
    Badge,
    Button,
    Card,
    Center,
    Container,
    Divider,
    Group,
    Menu,
    Modal,
    Pagination,
    Skeleton,
    Stack,
    Text,
    ThemeIcon,
    Title
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
    IconCalendarDollar,
    IconDotsVertical,
    IconEye,
    IconReceipt,
    IconRefresh,
    IconX,
} from "@tabler/icons-react";
import {
    MantineReactTable,
    MRT_ColumnDef,
    MRT_PaginationState,
} from "mantine-react-table";
import { MRT_Localization_FA } from "mantine-react-table/locales/fa/index.cjs";
import moment from "moment-jalaali";
import { useMemo, useState } from "react";
import { DateObject } from "react-multi-date-picker";
import { useNavigate } from "react-router-dom";
import AppointmentDetail from "../../../../modules/report/AppointmentDetail";
import CancelAppointmentQueue from "@/modules/report/CancelAppointmentQueue";
import FilterHeader from "@/modules/report/FilterHeader";
import PaymentReference from "@/modules/report/PaymentReference";
import useIsSmall from "@/hooks/useIsSmall";
import Header from "@/components/header";
import { useMarkOnlineBookingsSeen } from "@/modules/mobileHome/useSectionBadges";
import classes from "./appointmentDeposit.module.scss";


moment.locale("fa");
moment.loadPersian();

const money = (value?: number | null) =>
    typeof value === "number" ? `${value.toLocaleString("fa-IR")} ریال` : "ـ";

const appointmentStatus = (status?: AppointmentStatus) => {
    if (status === AppointmentStatus.Value0) return { label: "رزرو شده", color: "yellow" };
    if (status === AppointmentStatus.Value1) return { label: "نهایی شده", color: "green" };
    if (status === AppointmentStatus.Value2) return { label: "لغو شده", color: "red" };
    if (status === AppointmentStatus.Value3) return { label: "پرداخت ناموفق", color: "red" };
    if (status === AppointmentStatus.Value4) return { label: "بازگشت داده شده", color: "orange" };
    return { label: "نامشخص", color: "gray" };
};

const displayPaymentDate = (value?: string | null) => value && moment(value).isValid() ? moment(value).format("jYYYY/jMM/jDD - HH:mm") : "—";
function AppointmentDeposit() {
    useMarkOnlineBookingsSeen();
    const isSmall = useIsSmall();
    const navigate = useNavigate();
    const [opened, { open, close }] = useDisclosure(false);
    const [cancelOpened, { open: openCancel, close: closeCancel }] = useDisclosure(false);
    const [selectedAppointment, setSelectedAppointment] = useState<MerchantAppointmentQueueDto | null>(null);
    // No default date: the page starts with every booking, and a date filter is applied only when chosen.
    const [values, setValues] = useState<DateObject[]>([]);
    const [status, setStatus] = useState<string | null>(null);
    const [settlementStatus, setSettlementStatus] = useState<string | null>(null);
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerDto | null>(null);
    const [selectedTeam, setSelectedTeam] = useState<MerchantUserDto | null>(null);
    const [snappPayTransactionId, setSnappPayTransactionId] = useState("");
    const [appliedFilters, setAppliedFilters] = useState(() => ({
        values: [...values],
        status: null as string | null,
        settlementStatus: null as string | null,
        customer: null as CustomerDto | null,
        team: null as MerchantUserDto | null,
        snappPayTransactionId: "",
    }));

    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });


    const creationTimeFrom = appliedFilters.values[0]
        ? moment(new Date(appliedFilters.values[0].valueOf())).startOf("day").toISOString()
        : undefined;

    const creationTimeTo = appliedFilters.values[1]
        ? moment(new Date(appliedFilters.values[1].valueOf())).endOf("day").toISOString()
        : undefined;



    // A transaction id identifies one booking, so it is searched across all dates.
    const searchingTransaction = Boolean(appliedFilters.snappPayTransactionId);

    const hasSettlement =
        appliedFilters.settlementStatus === null ? undefined : appliedFilters.settlementStatus === "true";

    const { data, isLoading, isFetching, refetch } = useGetAppointmentDeposit({
        command: {
            Count: pagination?.pageSize,
            Skip: pagination?.pageIndex * pagination?.pageSize,
            Status: appliedFilters.status ? (Number(appliedFilters.status) as AppointmentStatus) : undefined,
            CustomerId: appliedFilters.customer?.id,
            MerchantUserId: appliedFilters.team?.id,
            CreationTimeFrom: searchingTransaction ? undefined : creationTimeFrom,
            CreationTimeTo: searchingTransaction ? undefined : creationTimeTo,
            HasSettlement: hasSettlement,
            SnappPayTransactionId: appliedFilters.snappPayTransactionId || undefined,
        },
    });

    const columns = useMemo<MRT_ColumnDef<MerchantAppointmentQueueDto>[]>(
        () => [
            {
                header: "تاریخ نوبت", id: "appointmentDate", size: 115,
                accessorFn: (row) => row.appointments?.[0]?.appointmentDate || "",
                Cell: ({ row }) => {
                    const item = row.original.appointments?.[0];
                    return <Stack gap={1} align="center"><Text size="xs" fw={700} ta="center">{item?.appointmentDate ? moment(item.appointmentDate).format("jYYYY/jMM/jDD") : "ـ"}</Text><Text size="xs" c="dimmed" ta="center">{item?.startTime ? moment(item.startTime, "HH:mm:ss").format("HH:mm") : "ساعت نامشخص"}</Text></Stack>;
                },
            },
            {
                header: "مشتری", id: "customer", size: 160,
                accessorFn: (row) => `${row.customer?.firstName || ""} ${row.customer?.lastName || ""}`.trim() || "ـ",
                Cell: ({ row }) => <Stack gap={1} align="center"><Text size="sm" fw={600} ta="center">{`${row.original.customer?.firstName || ""} ${row.original.customer?.lastName || ""}`.trim() || "ـ"}</Text><Text size="xs" c="dimmed" ta="center">{row.original.customer?.phoneNumber || "شماره ثبت نشده"}</Text></Stack>,
            },
            {
                header: "نوبت", id: "service", size: 180,
                accessorFn: (row) => row.appointments?.[0]?.merchantServiceTitle || "ـ",
                Cell: ({ row }) => { const item = row.original.appointments?.[0]; return <Stack gap={1} align="center"><Text size="sm" fw={650} lineClamp={1} ta="center">{item?.merchantServiceTitle || "ـ"}</Text><Text size="xs" c="dimmed" ta="center">با {`${item?.merchantUserFirstName || ""} ${item?.merchantUserLastName || ""}`.trim() || "آرایشگر نامشخص"}</Text></Stack>; },
            },
            {
                header: "تاریخ ثبت رزرو", id: "creationTime", size: 170,
                accessorFn: (row) => displayPaymentDate(row.creationTime),
            },
            {
                header: "زمان پرداخت درگاه", id: "gatewayPaymentDate", size: 170,
                accessorFn: (row) => displayPaymentDate(row.paymentInfo?.paymentDate),
            },
            {
                header: "روش پرداخت و پیگیری", id: "paymentMethod", size: 170,
                accessorFn: (row) => paymentTypeLabel(row),
                Cell: ({ row }) => <Stack gap={2} align="center"><Badge size="sm" variant="light" color={paymentMethodColor(row.original)}>{paymentTypeLabel(row.original)}</Badge>{row.original.paymentInfo?.rrn && <PaymentReference value={row.original.paymentInfo.rrn} size="xs" />}</Stack>,
            },
            {
                header: "وضعیت پرداخت", id: "paymentStatus", size: 145,
                accessorFn: (row) => paymentStatusInfo(row).label,
                Cell: ({ row }) => { const payment = paymentStatusInfo(row.original); return <Badge size="sm" color={payment.color} variant="light">{payment.label}</Badge>; },
            },
            {
                header: "تخفیف", accessorKey: "discountAmount", size: 110,
                Cell: ({ row }) => (row.original.discountAmount ?? 0) > 0 ? <Text size="xs" c="green.8" ta="center" title={row.original.discountCode || ""}>−{money(row.original.discountAmount)}</Text> : <Text size="xs" c="dimmed" ta="center">ـ</Text>,
            },
            {
                header: "مبلغ", accessorKey: "rateBreakdown.totalPrice", size: 125,
                Cell: ({ row }) => <Text size="xs" fw={800} ta="center">{money(row.original.feePayer != null || (row.original.payableAmount ?? 0) > 0 ? row.original.payableAmount : row.original.rateBreakdown?.totalPrice)}</Text>,
            },
        ],
        []
    );

    const table = useCustomTable({
        columns: columns,
        data: data?.result || [],
        localization: MRT_Localization_FA,
        rowCount: data?.count,
        autoResetPageIndex: false,
        enableGlobalFilter: false,
        enableTopToolbar: false,
        enableColumnFilters: false,
        enableSorting: false,
        manualPagination: true,
        enableRowActions: true,
        enablePagination: true,
        layoutMode: "grid-no-grow",
        initialState: { density: "xs" },
        onPaginationChange: setPagination,
        state: {
            isLoading: isLoading || isFetching,
            pagination,
        },
        displayColumnDefOptions: {
            "mrt-row-actions": { size: 48, grow: false },
        },
        mantineTableHeadCellProps: {
            className: classes.centeredHeader,
            style: { padding: "6px 8px", minHeight: 38, fontSize: 11, textAlign: "center" },
        },
        mantineTableBodyCellProps: {
            className: classes.centeredCell,
            style: { padding: "6px 8px", minHeight: 44, fontSize: 12, textAlign: "center" },
        },
        mantineBottomToolbarProps: {
            style: { minHeight: 48, fontSize: 12 },
        },
        mantineTableBodyRowProps: ({ row }) => ({
            style: { borderRight: `4px solid var(--mantine-color-${paymentStatusInfo(row.original).color}-6)` },
        }),

        renderRowActions: ({ row }) => (
            <Group wrap="nowrap" gap="xs">
                <Menu position="bottom-end" withinPortal>
                    <Menu.Target>
                        <ActionIcon variant="subtle" aria-label="عملیات نوبت">
                            <IconDotsVertical size={18} />
                        </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Item
                            color="teal"
                            disabled={!row.original.id || row.original.status === AppointmentStatus.Value2}
                            onClick={() => navigate(`/dashboard/payments/add/${row.original.appointments![0].appointmentId}`)}
                            leftSection={<IconReceipt size={16} />}
                        >
                            ثبت فاکتور
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                            onClick={() => {
                                setSelectedAppointment(row?.original);
                                open();
                            }}
                            leftSection={<IconEye size={14} />}
                        >
                            مشاهده همه جزئیات
                        </Menu.Item>
                        {row.original.bankGateway === BankGateway.Value4 ? <Menu.Item
                            color="red"
                            disabled={!row.original.id || row.original.status === AppointmentStatus.Value2}
                            onClick={() => {
                                setSelectedAppointment(row.original);
                                openCancel();
                            }}
                            leftSection={<IconX size={14} />}
                        >
                            لغو نوبت
                        </Menu.Item> : null}
                    </Menu.Dropdown>
                </Menu>
            </Group >
        ),
    });

    const content = (
        <Container size="full" px="sm" py="xs">
            <Stack gap="xs">
            <Group justify="space-between" align="flex-start" mb="xs">
                <Stack gap={0}>
                    <Title order={4}>نوبت‌ها و پرداخت‌ها</Title>
                    <Text size="sm" pt="xs">وضعیت رزرو، بیعانه، پرداخت مشتری و تسویه هر نوبت را یکجا بررسی کنید.</Text>
                </Stack>
                <Button variant="default" leftSection={<IconRefresh size={17} />} loading={isFetching} onClick={() => refetch()}>
                    تازه‌سازی
                </Button>
            </Group>
            <FilterHeader
                values={values}
                setValues={setValues}
                status={status}
                setStatus={setStatus}
                settlementStatus={settlementStatus}
                setSettlementStatus={setSettlementStatus}
                selectedCustomer={selectedCustomer}
                setSelectedCustomer={setSelectedCustomer}
                selectedTeam={selectedTeam}
                setSelectedTeam={setSelectedTeam}
                snappPayTransactionId={snappPayTransactionId}
                setSnappPayTransactionId={setSnappPayTransactionId}
                appliedFilters={appliedFilters}
                onRemoveFilter={(key) => {
                    if (key === "customer") setSelectedCustomer(null);
                    if (key === "team") setSelectedTeam(null);
                    if (key === "status") setStatus(null);
                    if (key === "settlement") setSettlementStatus(null);
                    if (key === "date") setValues([]);
                    if (key === "snappPay") setSnappPayTransactionId("");
                    setAppliedFilters((current) => ({
                        ...current,
                        ...(key === "customer" ? { customer: null } : {}),
                        ...(key === "team" ? { team: null } : {}),
                        ...(key === "status" ? { status: null } : {}),
                        ...(key === "settlement" ? { settlementStatus: null } : {}),
                        ...(key === "date" ? { values: [] } : {}),
                        ...(key === "snappPay" ? { snappPayTransactionId: "" } : {}),
                    }));
                    setPagination((current) => ({ ...current, pageIndex: 0 }));
                }}
                onFilter={() => {
                    setAppliedFilters({
                        values: [...values],
                        status,
                        settlementStatus,
                        customer: selectedCustomer,
                        team: selectedTeam,
                        snappPayTransactionId: snappPayTransactionId.trim(),
                    });
                    setPagination((prev) => ({
                        ...prev,
                        pageIndex: 0,
                    }));
                }}
            />
            {isSmall ? (
                isLoading ? <Stack mt="md">{[1, 2, 3].map((item) => <Skeleton key={item} h={230} radius="md" />)}</Stack>
                    : (data?.result?.length || 0) === 0 ? <Center py="xl"><Text c="dimmed">نوبتی با این فیلترها پیدا نشد.</Text></Center>
                        : <Stack mt="md" gap="sm">{data?.result?.map((appointment) => <AppointmentCard key={appointment.id} appointment={appointment} onCreateInvoice={() => appointment.id && navigate(`/dashboard/payments/add/${appointment.id}`)} onDetails={() => { setSelectedAppointment(appointment); open(); }} onCancel={() => { setSelectedAppointment(appointment); openCancel(); }} />)}{(data?.count || 0) > pagination.pageSize && <Pagination mt="sm" mx="auto" value={pagination.pageIndex + 1} total={Math.ceil((data?.count || 0) / pagination.pageSize)} onChange={(page) => setPagination((current) => ({ ...current, pageIndex: page - 1 }))} />}</Stack>
            ) : <MantineReactTable table={table} />}
            <Modal opened={opened} onClose={close} title="جزئیات نوبت و پرداخت" size="lg" fullScreen={isSmall}>
                <AppointmentDetail appointment={selectedAppointment} />
            </Modal>
            <CancelAppointmentQueue appointmentQueueId={selectedAppointment?.id || 0} close={closeCancel} opened={cancelOpened} />
            </Stack>
        </Container>
    );

    return isSmall ? <><Header title="نوبت‌های آنلاین" showBack route="/dashboard" />{content}</> : content;
}

function AppointmentCard({ appointment, onCreateInvoice, onDetails, onCancel }: { appointment: MerchantAppointmentQueueDto; onCreateInvoice: () => void; onDetails: () => void; onCancel: () => void }) {
    const status = appointmentStatus(appointment.status);
    const payment = paymentStatusInfo(appointment);
    const firstItem = appointment.appointments?.[0];
    const customerName = `${appointment.customer?.firstName || ""} ${appointment.customer?.lastName || ""}`.trim() || "مشتری بدون نام";
    return <Card withBorder radius="md" p="md"><Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap"><Group gap="sm" wrap="nowrap"><ThemeIcon color="teal" variant="light" radius="md"><IconCalendarDollar size={19} /></ThemeIcon><Stack gap={1}><Text fw={800}>{customerName}</Text><Text size="xs" c="dimmed">{firstItem?.appointmentDate ? moment(firstItem.appointmentDate).format("jYYYY/jMM/jDD") : "تاریخ نامشخص"}</Text></Stack></Group><Badge color={status.color} variant="light">{status.label}</Badge></Group>
        <Divider />
        <Group justify="space-between"><Text size="sm" c="dimmed">خدمت</Text><Text size="sm" fw={700}>{firstItem?.merchantServiceTitle || "ـ"}</Text></Group>
        <Group justify="space-between"><Text size="sm" c="dimmed">آرایشگر</Text><Text size="sm">{`${firstItem?.merchantUserFirstName || ""} ${firstItem?.merchantUserLastName || ""}`.trim() || "ـ"}</Text></Group>
        <Group justify="space-between"><Text size="sm" c="dimmed">تاریخ ثبت رزرو</Text><Text size="sm">{displayPaymentDate(appointment.creationTime)}</Text></Group>
        <Group justify="space-between"><Text size="sm" c="dimmed">زمان پرداخت درگاه</Text><Text size="sm">{displayPaymentDate(appointment.paymentInfo?.paymentDate)}</Text></Group>
        <Group justify="space-between"><Text size="sm" c="dimmed">روش پرداخت</Text><Badge size="sm" variant="light" color={paymentMethodColor(appointment)}>{paymentTypeLabel(appointment)}</Badge></Group>
        <Group justify="space-between"><Text size="sm" c="dimmed">وضعیت پرداخت</Text><Badge size="lg" variant="light" color={payment.color}>{payment.label}</Badge></Group>
        <Group justify="space-between" wrap="nowrap"><Text size="sm" c="dimmed">شماره پیگیری</Text><PaymentReference value={appointment.paymentInfo?.rrn} /></Group>
        <Group justify="space-between"><Text size="sm" c="dimmed">مبلغ پرداختی مشتری</Text><Text fw={800}>{money(appointment.feePayer != null || (appointment.payableAmount ?? 0) > 0 ? appointment.payableAmount : appointment.rateBreakdown?.totalPrice)}</Text></Group>
        {(appointment.discountAmount ?? 0) > 0 && <Group justify="space-between"><Text size="sm" c="dimmed">تخفیف{appointment.discountCode ? ` (${appointment.discountCode})` : ""}</Text><Text size="sm" c="green.8">−{money(appointment.discountAmount)}</Text></Group>}
        {(appointment.customerFeeAmount ?? 0) > 0 && <Group justify="space-between"><Text size="sm" c="dimmed">کارمزد مشتری</Text><Text size="sm">{money(appointment.customerFeeAmount)}</Text></Group>}
        {(appointment.merchantFeeAmount ?? 0) > 0 && <Group justify="space-between"><Text size="sm" c="dimmed">کارمزد سهم شما</Text><Text size="sm" c="red.7">{money(appointment.merchantFeeAmount)}</Text></Group>}
        <Button color="teal" leftSection={<IconReceipt size={16} />} disabled={!appointment.id || appointment.status === AppointmentStatus.Value2} onClick={onCreateInvoice}>ثبت فاکتور</Button>
        {appointment.bankGateway === BankGateway.Value4 ? <Button color="red" variant="light" leftSection={<IconX size={16} />} disabled={!appointment.id || appointment.status === AppointmentStatus.Value2} onClick={onCancel}>لغو نوبت</Button> : null}
        <Button variant="light" leftSection={<IconEye size={16} />} onClick={onDetails}>مشاهده همه جزئیات</Button>
    </Stack></Card>;
}

export default AppointmentDeposit;
